import { useCallback, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useQueryClient } from 'react-query'

import {
  MerchantBankAccountTypes,
  UpdateMerchantBankAccountTypes,
  UpdateMerchantBankAccountFormTypes,
  UpdateMerchantBankAccountFormSubmitValuesTypes,
  UpdateMerchantBankAccountFormSubmitActionsTypes,
} from 'types/merchant'

import useMerchantStore from 'store/merchant'

const UPDATEMERCHANTBANKACCOUNTINITVAL: UpdateMerchantBankAccountTypes = {
  traderId: 0,
  bankAccountNumber: 0,
  bankAccountTypeId: 0,
  name: '',
  expiryDate: '',
  safetyCode: null,
  payerPartyCode: '',
  payerPartyParticulars: '',
  payeePartyRef: '',
  payeePartyCode: '',
  payeePartyParticulars: '',
  isDefaultBankAccount: 0,
}

export const useUpdateBankAccount = (
  selectedMerchantBankAccount: MerchantBankAccountTypes,
  onCloseModal: () => void
): {
  isLoading: boolean
  initialValues: UpdateMerchantBankAccountTypes
  handleSubmitUpdateMerchantBankAccount: (
    values: UpdateMerchantBankAccountFormSubmitValuesTypes,
    actions: UpdateMerchantBankAccountFormSubmitActionsTypes
  ) => void
} => {
  const { traderId } = useParams()
  const queryClient = useQueryClient()
  const merchantTraderId = Number(traderId) || 0
  // MERCHANT UPDATE BANK ACCOUNT START
  const updateMerchantBankAccount = useMerchantStore(
    (state) => state.updateMerchantBankAccounts
  )
  const merchantBankAccountUpdateLoading = useMerchantStore(
    (state) => state.merchantBankAccountsUpdateLoading
  )
  // MERCHANT UPDATE BANK ACCOUNT END
  // MISC STATES START
  const [initialValues, initialValuesSet] =
    useState<UpdateMerchantBankAccountFormTypes>({
      ...UPDATEMERCHANTBANKACCOUNTINITVAL,
      traderId: merchantTraderId,
      bankAccountNumber: Number(
        selectedMerchantBankAccount.bankAccountNumber.replace(/-/g, '')
      ),
      bankAccountTypeId: selectedMerchantBankAccount.bankAccountTypeId,
      name: selectedMerchantBankAccount.name,
      expiryDate: selectedMerchantBankAccount.expiryDate,
      safetyCode: selectedMerchantBankAccount.safetyCode,
      payerPartyCode: selectedMerchantBankAccount.payerPartyCode,
      payerPartyParticulars: selectedMerchantBankAccount.payerPartyParticulars,
      payeePartyRef: selectedMerchantBankAccount.payeePartyRef,
      payeePartyCode: selectedMerchantBankAccount.payeePartyCode,
      payeePartyParticulars: selectedMerchantBankAccount.payeePartyParticulars,
      isDefaultBankAccount: selectedMerchantBankAccount.isDefaultBankAccount,
    })
  // MISC STATES END

  const isLoading = useMemo(() => {
    return merchantBankAccountUpdateLoading ? true : false
  }, [merchantBankAccountUpdateLoading])

  const handleSubmitUpdateMerchantBankAccount = useCallback(
    async (
      values: UpdateMerchantBankAccountFormSubmitValuesTypes,
      actions: UpdateMerchantBankAccountFormSubmitActionsTypes
    ) => {
      try {
        await updateMerchantBankAccount(merchantTraderId, values)
        toast.success('Merchant Bank Account update successful.')
        queryClient.invalidateQueries({ queryKey: ['useMerchantBankAccounts'] })
        onCloseModal()
      } catch (error) {
        const message = JSON.parse(JSON.stringify(error))
        toast.error(message.outcomeUserMessage)
      }
    },
    [updateMerchantBankAccount]
  )

  return {
    isLoading,
    initialValues,
    handleSubmitUpdateMerchantBankAccount,
  }
}
